import { Box, ButtonBase, Stack, Typography, Drawer, Slide } from "@mui/material"
import { alpha, styled } from "@mui/material/styles"
import type { FC } from ".."
import {
  Link,
  SvgIcon,
  ResponsiveImage,
  FilmFestivalLogoSimple,
  GowayLogo,
  RulesAndRequirements,
  FilmFestivalFooter,
} from ".."
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"

interface SideDrawerProps {
  variant: "pro" | "globetrotter" | "terms"
  open: boolean
  onClose: () => void
  handleOpenTermsModal?: () => void
  children: React.ReactNode
}

const SideDrawer: FC<SideDrawerProps> = ({
  variant,
  open,
  onClose,
  children,
  handleOpenTermsModal,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlideProps={{
        timeout: { enter: 600, exit: 400 },
        easing: {
          enter: "cubic-bezier(1, 0, 0.5, 1)",
          exit: "cubic-bezier(1, 0, 0.5, 1)",
        },
      }}
      PaperProps={{
        sx: {
          zIndex: variant === "terms" ? 1500 : 1000,
          margin: 0,
        },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          backgroundColor: (theme) => theme.palette["tertiary.800"].main,
        },
      }}
    >
      <Box
        position="relative"
        display="grid"
        gridTemplateColumns={{ xs: "1fr", lg: "minmax(300px, 480px) 1fr" }}
      >
        {variant === "terms" ? <TermsHeader /> : <DrawerInfoSection variant={variant} />}
        <Box position="relative" width={{ xs: "100vw", lg: "auto" }}>
          <Box position="fixed" top={{ xs: 30, lg: 40 }} right={{ xs: 20, lg: 40 }} zIndex={1000}>
            <BackButton onClick={onClose} />
          </Box>
          <Stack
            position="relative"
            gap={8}
            maxWidth="700px"
            margin="0 auto"
            padding={10}
            sx={(theme) => ({
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(8, 6),
                borderTop: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
              },
            })}
          >
            {children}
          </Stack>
        </Box>
        <Stack zIndex={{ xs: 1, lg: 1000 }} position="relative" gridColumn="1 / -1">
          <RulesAndRequirements onOpenTermsModal={handleOpenTermsModal} />
          <FilmFestivalFooter handleOpenTermsModal={handleOpenTermsModal!} />
        </Stack>
      </Box>
    </Drawer>
  )
}

export default SideDrawer

// A sub component for the drawer info section
const DrawerInfoSection: FC<{ variant: "pro" | "globetrotter" | "terms" }> = ({ variant }) => {
  const { drawerContent } = filmFestivalGlobals

  const getContent = () => {
    switch (variant) {
      case "pro":
        return drawerContent.pro
      case "globetrotter":
        return drawerContent.globetrotter
      case "terms":
      default:
        return null
    }
  }
  const globals = getContent()

  const image = {
    variants: [
      {
        width: 127,
        height: 127,
        url: globals?.imageUrl as string,
        title: "",
        alt: "test",
        style: "default",
      },
    ],
  }

  return (
    <Stack
      sx={(theme) => ({
        borderRight: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
        padding: { xs: theme.spacing(6, 6, 8), md: theme.spacing(10) },
        gap: theme.spacing(10),
        height: { xs: "auto", lg: "100vh" },
        position: { xs: "relative", lg: "sticky" },
        top: 0,
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center", lg: "flex-start" },
        transition: "all 0.3s ease-in-out",
      })}
    >
      <Stack>
        <Box display="flex" gap={{ xs: 5, lg: 8 }} alignItems="center" mb={{ xs: 12, lg: 10 }}>
          <FilmFestivalLogoSimple />
          <GowayLogo sx={{ width: 91 }} />
        </Box>
        {/* Intro */}
        <Stack
          gap={5}
          direction={{ xs: "row", md: "column" }}
          alignItems={{ xs: "center", md: "flex-start" }}
        >
          <Box
            position="relative"
            width={{ xs: "96px", md: "132px" }}
            height={{ xs: "96px", md: "132px" }}
            flexShrink={0}
          >
            <ResponsiveImage
              image={image}
              sx={(theme) => ({
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: theme.spacing(4),
              })}
            />
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "#FF8F52",
                mixBlendMode: "overlay",
                opacity: 0.4,
                borderRadius: theme.spacing(4),
              })}
            />
          </Box>
          <Box display="flex" maxWidth="370px">
            <StyledTypography variant="heading.md" color="neutral.700">
              {globals?.title1}
              <span
                style={{
                  display: "inline-block",
                  fontWeight: 400,
                  fontStyle: "italic",
                  padding: "0 4px",
                }}
              >
                {globals?.keyword1}
              </span>
              {globals?.title2}
              <span
                style={{
                  display: "inline-block",
                  fontWeight: 400,
                  fontStyle: "italic",
                  paddingLeft: 4,
                }}
              >
                {globals?.keyword2}
              </span>
            </StyledTypography>
          </Box>
        </Stack>
      </Stack>
      {/* Prizes */}
      <Stack gap={{ xs: 6, md: 10 }}>
        {globals?.prizes.map((prize) => (
          <Prize key={prize.title} prize={prize} />
        ))}
      </Stack>
    </Stack>
  )
}

// A sub component for the typography
const StyledTypography = styled(Typography)(
  ({ theme }) => `
    color: ${theme.palette["neutral.700"].main};
    font-size: 32px!important;
    font-weight: 700;
    line-height: 110%!important;
  `
)

// A sub component for the back button
const BackButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        ...theme.typography["link.md"],
        "display": "flex",
        "alignItems": "center",
        "gap": 2,
        "minHeight": { xs: 40, lg: 49 },
        "minWidth": { xs: 82, lg: 84 },
        "padding": theme.spacing(2, 4),
        "backgroundColor": theme.palette["neutral.700"].main,
        "borderRadius": 999,
        "transition": theme.transitions.create(["background-color", "border-color", "color"]),
        "zIndex": 1000,
        "&:hover": theme.palette["neutral.800"].main,
      })}
    >
      <Typography
        variant="body.md"
        fontWeight={600}
        sx={(theme) => ({
          color: theme.palette["tertiary.800"].main,
          lineHeight: "160%!important",
          textTransform: "uppercase",
          fontSize: "13px",
        })}
      >
        Back
      </Typography>
    </ButtonBase>
  )
}

interface Prize {
  title: string
  items: string[]
}

// A sub component for the prizes
const Prize: FC<{ prize: Prize }> = ({ prize }) => {
  return (
    <Stack gap={{ xs: 4, md: 1 }}>
      <StyledTypography
        variant="heading.md"
        sx={(theme) => ({
          color: theme.palette["primary.800"].main,
          fontSize: { xs: "24px!important", md: 32 },
          fontWeight: 700,
          lineHeight: "120%",
        })}
      >
        {prize.title}
      </StyledTypography>
      <Stack spacing={1}>
        {prize.items.map((item) => (
          <Typography
            key={item}
            variant="body.md"
            sx={(theme) => ({
              "color": theme.palette["neutral.700"].main,
              "fontSize": "16px!important",
              "fontWeight": 600,
              "pl": 4,
              "position": "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: { xs: "14px", md: "15px" },
                transform: "translateY(-50%)",
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: (theme) => theme.palette["primary.800"].main,
              },
            })}
          >
            {item}
          </Typography>
        ))}
      </Stack>
    </Stack>
  )
}

const TermsHeader: FC = () => {
  const { terms } = filmFestivalGlobals.drawerContent
  return (
    <Stack
      sx={(theme) => ({
        borderRight: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
        padding: { xs: theme.spacing(6, 6, 8), md: theme.spacing(10) },
        gap: theme.spacing(10),
        height: { xs: "auto", lg: "100vh" },
        position: { xs: "relative", lg: "sticky" },
        top: 0,
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center", lg: "flex-start" },
        transition: "all 0.3s ease-in-out",
      })}
    >
      <Stack>
        <Box display="flex" gap={{ xs: 5, lg: 8 }} alignItems="center" mb={{ xs: 12, lg: 10 }}>
          <FilmFestivalLogoSimple />
          <GowayLogo sx={{ width: 91 }} />
        </Box>
        <StyledTypography
          variant="heading.md"
          sx={(theme) => ({
            color: theme.palette["primary.800"].main,
            fontSize: { xs: "32px!important", md: "56px!important" },
            fontWeight: 700,
            lineHeight: "110%",
          })}
        >
          {terms.title}
        </StyledTypography>
        <StyledTypography
          variant="body.md"
          sx={(theme) => ({
            color: theme.palette["neutral.700"].main,
            fontSize: "18px!important",
            fontWeight: 400,
            lineHeight: "150%",
            maxWidth: "356px",
            marginTop: theme.spacing(3),
          })}
        >
          {terms.subtitle}
        </StyledTypography>
      </Stack>
    </Stack>
  )
}

const TermsContent: FC = () => {
  const { terms } = filmFestivalGlobals.drawerContent
  return (
    <Stack>
      {terms.descriptionItems.map((item) => (
        <Typography key={item}>{item}</Typography>
      ))}
    </Stack>
  )
}
